import React from 'react';
import { DeleteWarning, Help, Modal, SaveButton, SystemText, TitleBar } from 'e4shared';
import { useDispatch, useSelector } from 'react-redux';
import { setShowSuccess } from 'actions/ui';
import { apiPatch, apiPost } from 'services';
import { Notify } from './components/notify';
import posthog from 'posthog-js';

export const ManageDashboards = ({ show, close, refresh, dashboards }) => {
    const dispatch = useDispatch();
    const [saving, setSaving] = React.useState(false);
    const [showErrors, setShowErrors] = React.useState(false);
    const [showNotify, setShowNotify] = React.useState(false);
    const [showDeleteWarning, setShowDeleteWarning] = React.useState(false);
    const [values, setValues] = React.useState(dashboards);

    const user = useSelector(state => state.user.user);

    React.useEffect(() => {
        setValues(dashboards);
    }, [show]);

    const text = {
        title: SystemText('Dashboard/Manage/Title'),
        yourBoards: SystemText('Dashboard/Manage/YourBoards'),
        companyBoards: SystemText('Dashboard/Manage/CompanyBoards'),
        copySuffix: SystemText('Dashboard/Manage/CopySuffix'),
        whatIsThis: SystemText('Dashboard/Manage/WhatIsThis'),
        whatIsThisHelp: SystemText('Dashboard/Manage/WhatIsThisHelp'),
        warning: SystemText('Dashboard/Manage/Warning'),
        buttons: {
            save: SystemText('Dashboard/Manage/Buttons/Save'),
            close: SystemText('Dashboard/Manage/Buttons/Close'),
            default: SystemText('Dashboard/Manage/Buttons/Default'),
            copy: SystemText('Dashboard/Manage/Buttons/Copy'),
            notify: SystemText('Dashboard/Manage/Buttons/Notify'),
            delete: SystemText('Dashboard/Manage/Buttons/Delete'),
            addNew: SystemText('Dashboard/Manage/Buttons/AddNew'),
            addPersonal: SystemText('Dashboard/Manage/Buttons/AddPersonal'),
            addCompany: SystemText('Dashboard/Manage/Buttons/AddCompany'),
        },
        fields: {
            name: {
                placeholder: SystemText('Dashboard/Manage/Fields/Name/Placeholder'),
                error: SystemText('Dashboard/Manage/Fields/Name/Error'),
                lengthError: SystemText('Dashboard/Manage/Fields/Name/Error/Length'),
            },
        },
        success: {
            single: SystemText('Dashboard/Manage/Success/Singular'),
            plural: SystemText('Dashboard/Manage/Success/Plural'),
            notify: SystemText('Dashboard/Manage/Success/Notify'),
        },
    };

    const save = () => {
        const errors = values.some(v => !v.name);
        const lengthErrors = !errors && values.some(v => v.name?.length > 50);

        if (errors || lengthErrors) {
            setShowErrors(true);
            setSaving(false);
        } else {
            const processed = values.map(v => {
                return { ...v, id: v.id.toString().indexOf('TEMP') >= 0 ? 0 : v.id };
            });

            apiPatch('dashboards', processed, user.guid, () => {
                dispatch(setShowSuccess(values.length === 1 ? text.success.single : text.success.plural));
                setSaving(false);
                posthog.capture('dashboards-saved', { total: values.length });
                refresh();
                close();
            });
        }
    };

    const send = (message, id) => {
        apiPost('dashboard-notify', { id, message }, user.guid, () => {
            dispatch(setShowSuccess(text.success.notify));
        });
    };

    const footer = (
        <div className="flex flex-center">
            <div className="flex-grow">
                {dashboards.some(d => (d.assigned_Ids || []).some(x => x === user.id)) && (
                    <div className="flex flex-center">
                        <i className="fa fa-building" />
                        <span className="ml10">{text.whatIsThis}</span>
                        <Help id="whatisthis">{text.whatIsThisHelp}</Help>
                    </div>
                )}
            </div>
            <div className="pull-right">
                <SaveButton
                    data-testid="button-save"
                    saving={saving}
                    setSaving={setSaving}
                    className="btn btn-success"
                    onClick={save}
                >
                    {text.buttons.save}
                </SaveButton>
                <button data-testid="button-close" className="btn" onClick={close}>
                    {text.buttons.close}
                </button>
            </div>
        </div>
    );

    const addNewBoard = company => {
        setValues(
            [...values].concat({
                id: `TEMP-${values.length}`,
                name: '',
                default: false,
                company,
                profile_Id: company ? 0 : user.id,
            })
        );
    };

    const copyBoard = board => {
        const newValues = [...values].concat([
            {
                id: `TEMP-${values.length}`,
                origin_Id: board.id,
                name: `${board.name}${text.copySuffix}`,
                profile_Id: user.administrator ? board.profile_Id : user.id,
                default: false,
                company: user.administrator ? board.company : false,
            },
        ]);

        setValues(newValues);
    };

    const deleteBoard = id => {
        const boards = [...values].filter(b => b.id !== id);
        setValues(boards);
    };

    const setText = (id, text) => {
        const boards = [...values];
        const board = boards.find(b => b.id === id);
        board.name = text;
        setValues(boards);
    };

    const setDefaultBoard = id => {
        const boards = [...values].map(b => ({ ...b, default: false }));
        const board = boards.find(b => b.id === id);
        board.default = true;
        setValues(boards);
    };

    const showCompanyBoards = user.administrator && values.find(d => !d.profile_Id);

    const boardRow = (d, i, company) => (
        <div key={`${d.id}-${i}`} className="flex flex-center mt5">
            <div
                style={{ position: 'relative' }}
                className={`relative flex-grow mr10 form-groupx ${showErrors && !d.name ? 'has-error' : ''}`}
            >
                <input
                    type="text"
                    maxLength="50"
                    value={d.name}
                    disabled={!d.profile_Id && !user.administrator}
                    placeholder={text.fields.name.placeholder}
                    className="form-control"
                    onChange={e => setText(d.id, e.target.value)}
                />
                {!d.profile_Id && !company && <i className="fa fa-building form-control-feedback" />}
            </div>
            <div>
                {company ? (
                    <button
                        data-testid={`button-notify-${d.id}`}
                        className={`btn btn-xs btn-${d.default ? 'success' : 'primary'} mr5`}
                        onClick={() => setShowNotify(d.id)}
                        title={text.buttons.notify}
                    >
                        <i className="fa fa-envelope" />
                    </button>
                ) : (
                    <button
                        data-testid={`button-default-${d.id}`}
                        className={`btn btn-xs btn-${d.default ? 'success btn-power' : 'primary'} mr5`}
                        onClick={() => setDefaultBoard(d.id)}
                        title={text.buttons.default}
                    >
                        <i className="fa fa-dashboard" />
                    </button>
                )}
                <button
                    data-testid={`button-copy-${d.id}`}
                    className="btn btn-xs btn-warning mr5"
                    disabled={!d.id}
                    onClick={() => copyBoard(d)}
                    title={text.buttons.copy}
                >
                    <i className="fa fa-copy" />
                </button>
                <button
                    data-testid={`button-delete-${d.id}`}
                    disabled={d.default || (!user.administrator && !d.profile_Id) || (!company && !d.profile_Id)}
                    className="btn btn-xs btn-danger"
                    onClick={() => {
                        setShowDeleteWarning(d.id);
                    }}
                    title={text.buttons.delete}
                >
                    <i className="fa fa-trash" />
                </button>
            </div>
        </div>
    );

    return (
        <Modal title={text.title} show={show} close={close} footer={footer}>
            <div className="clear-titles">
                {showErrors && values.some(v => !v.name) && <div className="text-danger mb10">{text.fields.name.error}</div>}
                {showErrors && !values.some(v => !v.name) && values.some(v => v.name.length > 50) && (
                    <div className="text-danger mb10">{text.fields.name.lengthError}</div>
                )}
                {showCompanyBoards && <TitleBar title={text.yourBoards} suffix={<i className="fa fa-user ml10" />} />}
                {values
                    .filter(d => d.profile_Id || (d.assigned_Ids || []).some(x => x === user.id))
                    .map((d, i) => {
                        return boardRow(d, i);
                    })}
                {showCompanyBoards && (
                    <div className="pt10">
                        <TitleBar title={text.companyBoards} suffix={<i className="fa fa-building ml10" />} />
                        {values
                            .filter(d => !d.profile_Id)
                            .map((d, i) => {
                                return boardRow(d, i, true);
                            })}
                    </div>
                )}
                <div className="mt10">
                    {user.administrator ? (
                        <>
                            <button data-testid="button-add" onClick={() => addNewBoard(false)} className="btn btn-link">
                                {text.buttons.addPersonal}
                            </button>
                            <span className="mr5 ml5">|</span>
                            <button
                                data-testid="button-add-company"
                                onClick={() => addNewBoard(true)}
                                className="btn btn-link"
                            >
                                {text.buttons.addCompany}
                            </button>
                        </>
                    ) : (
                        <button data-testid="button-add" onClick={() => addNewBoard()} className="btn btn-link">
                            {text.buttons.addNew}
                        </button>
                    )}
                </div>
            </div>
            <DeleteWarning
                message={text.warning}
                show={showDeleteWarning}
                close={confirm => {
                    confirm && deleteBoard(showDeleteWarning);
                    setShowDeleteWarning(false);
                }}
            />
            <Notify
                show={showNotify}
                close={() => setShowNotify(false)}
                send={msg => {
                    send(msg, showNotify);
                    setShowNotify(false);
                }}
            />
        </Modal>
    );
};

export default ManageDashboards;
